import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BellRing, Calendar, CheckCircle2, Clock3, Eye, Filter, Percent, RefreshCcw, Users, X, XCircle } from "lucide-react";
import Swal from "sweetalert2";
import "../../Formation/ResponsableFormation/RessourcesStyles/ConsolidationDemandes.css";

import { API_BASE_URL, getRequest, postRequest } from "../../../../api";
import { fetchCampagnesRequest } from "../../../redux/actions/campagnesActions";
import { fetchSkillEvalsByCampagneRequest } from "../../../redux/actions/skillEvaluationActions";

const norm = (v) => String(v ?? "").trim().toLowerCase();

const uniqueOptions = (values) =>
    [...new Set((values || []).filter(Boolean).map((v) => String(v).trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));

const safeDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateFR = (iso) => {
    const d = safeDate(iso);
    if (!d) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
};

const statusLabel = (code) => {
    const st = String(code || "").toUpperCase();
    if (st === "EN_ATTENTE_COLLAB") return "En attente collaborateur";
    if (st === "EN_ATTENTE_MANAGER") return "En attente manager";
    if (st === "EN_ATTENTE_N2") return "En attente N+2";
    if (st === "VALIDEE") return "Validée";
    if (st === "REJETEE") return "Rejetée";
    if (st === "PROPOSE") return "Proposée";
    return st || "-";
};

const statusClass = (code) => {
    const st = String(code || "").toUpperCase();
    if (st === "VALIDEE") return "st-treated";
    if (st === "REJETEE") return "st-rejected";
    if (st === "EN_ATTENTE_MANAGER" || st === "EN_ATTENTE_N2") return "st-manager";
    if (st === "EN_ATTENTE_COLLAB" || st === "PROPOSE") return "st-pending";
    return "st-default";
};

const userName = (u) =>
    u?.fullName || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.email || `Utilisateur ${u?.id}`;

const averageScore = (niveaux = []) => {
    const values = niveaux.map((n) => Number(n.scoreObtenu)).filter((n) => Number.isFinite(n));
    if (!values.length) return null;
    return Math.round((values.reduce((sum, n) => sum + n, 0) / values.length) * 10) / 10;
};

const DetailField = ({ label, value }) => (
    <div className="detailField">
        <div className="detailFieldLabel">{label}</div>
        <div className="detailFieldValue">{value ?? <span className="detailNull">-</span>}</div>
    </div>
);

const ModalShell = ({ open, title, children, onClose, footer }) => {
    if (!open) return null;
    return (
        <div className="consModal" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className="consModalPanel">
                <div className="consModalHeader">
                    <div className="consModalTitle">{title}</div>
                    <button className="iconClose" type="button" onClick={onClose} title="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="consModalBody">{children}</div>
                {footer ? <div className="consModalFooter">{footer}</div> : null}
            </div>
        </div>
    );
};

const EvaluationDetailView = ({ data }) => {
    if (!data) return null;
    return (
        <div className="demandeDetailWrap">
            <div className="detailHero">
                <div className="detailHeroLeft">
                    <div className="detailHeroAvatar">{(data.userFullName || "?")[0].toUpperCase()}</div>
                    <div>
                        <div className="detailHeroName">{data.userFullName || "-"}</div>
                        <div className="detailHeroSub">{data.campagneNom || "-"} · {data.jobName || "Poste non renseigné"}</div>
                    </div>
                </div>
                <div className={statusClass(data.statut)}>{statusLabel(data.statut)}</div>
            </div>

            <div className="detailSection">
                <div className="detailSectionTitle">Evaluation</div>
                <div className="detailSectionGrid">
                    <DetailField label="Campagne" value={data.campagneNom} />
                    <DetailField label="Référence" value={data.campagneReference} />
                    <DetailField label="Collaborateur" value={data.userFullName} />
                    <DetailField label="Matricule" value={data.userEmployeeCode} />
                    <DetailField label="Evaluateur" value={data.evaluateurFullName || "Auto-évaluation"} />
                    <DetailField label="Entité" value={data.entityName} />
                    <DetailField label="Poste" value={data.jobName} />
                    <DetailField label="Score moyen" value={averageScore(data.niveaux) ?? "-"} />
                </div>
            </div>

            <div className="detailSection">
                <div className="detailSectionTitle">Workflow</div>
                <div className="detailSectionGrid">
                    <DetailField label="Statut manager" value={data.managerStepStatus} />
                    <DetailField label="Décision manager par" value={data.managerDecisionByName} />
                    <DetailField label="Décision manager le" value={data.managerDecisionAt ? fmtDateFR(data.managerDecisionAt) : null} />
                    <DetailField label="Commentaire manager" value={data.managerComment} />
                    <DetailField label="Statut N+2" value={data.n2StepStatus} />
                    <DetailField label="Décision N+2 par" value={data.n2DecisionByName} />
                    <DetailField label="Décision N+2 le" value={data.n2DecisionAt ? fmtDateFR(data.n2DecisionAt) : null} />
                    <DetailField label="Commentaire N+2" value={data.n2Comment} />
                </div>
            </div>

            <div className="miniTableWrap">
                <table className="miniTable">
                    <thead>
                    <tr>
                        <th>Compétence</th>
                        <th>Domaine</th>
                        <th className="center">Collab</th>
                        <th className="center">Manager</th>
                        <th className="center">N+2</th>
                        <th className="center">Score</th>
                        <th>Commentaire</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(data.niveaux || []).map((n) => (
                        <tr key={n.id || n.skillId}>
                            <td>{n.skillNom || n.skillCode || "-"}</td>
                            <td>{n.domaineNom || "-"}</td>
                            <td className="center">{n.scoreCollab ?? "-"}</td>
                            <td className="center">{n.scoreManager ?? "-"}</td>
                            <td className="center">{n.scoreN2 ?? "-"}</td>
                            <td className="center">{n.scoreObtenu ?? "-"}</td>
                            <td>{n.commentaire || "-"}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default function ConsolidationSkills() {
    const dispatch = useDispatch();
    const campagnesState = useSelector((s) => s.campagnes || {});
    const skillState = useSelector((s) => s.skillEvaluation || {});
    const campagnesRaw = campagnesState.list;
    const evaluations = skillState.skillEvaluations;
    const loading = !!skillState.loading;

    const campagnes = useMemo(() => {
        return (campagnesRaw || [])
            .filter((c) => String(c?.typeCampagne || "").toUpperCase() === "SKILLS")
            .sort((a, b) => (a?.status === b?.status ? 0 : a?.status === true ? -1 : 1));
    }, [campagnesRaw]);

    const [campagneId, setCampagneId] = useState("");
    const [participation, setParticipation] = useState(null);
    const [participationLoading, setParticipationLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [collaborateur, setCollaborateur] = useState("");
    const [reference, setReference] = useState("");
    const [domaine, setDomaine] = useState("");
    const [theme, setTheme] = useState("");
    const [competence, setCompetence] = useState("");
    const [statut, setStatut] = useState("");
    const [dateEvaluation, setDateEvaluation] = useState("");
    const [page, setPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openUsersModal, setOpenUsersModal] = useState(false);
    const [openParticipationModal, setOpenParticipationModal] = useState(false);
    const [openStatsModal, setOpenStatsModal] = useState(false);
    const [openProlonger, setOpenProlonger] = useState(false);
    const [openRelancer, setOpenRelancer] = useState(false);
    const [newEndDate, setNewEndDate] = useState("");
    const [prolongerComment, setProlongerComment] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    const [statsModalTitle, setStatsModalTitle] = useState("");

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (!campagnes.length) {
            setCampagneId("");
            return;
        }
        if (!campagneId || !campagnes.some((c) => String(c.id) === String(campagneId))) {
            const defaultCampagne = campagnes.find((c) => c?.isDefault) || campagnes.find((c) => c?.status === true) || campagnes[0];
            setCampagneId(String(defaultCampagne?.id || ""));
        }
    }, [campagnes, campagneId]);

    const selectedCampagne = useMemo(
        () => campagnes.find((c) => String(c.id) === String(campagneId)) || null,
        [campagnes, campagneId]
    );

    const refreshData = useCallback(() => {
        if (!campagneId) return;
        dispatch(fetchSkillEvalsByCampagneRequest(Number(campagneId)));
    }, [dispatch, campagneId]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    useEffect(() => {
        let cancelled = false;
        const loadParticipation = async () => {
            if (!campagneId) {
                setParticipation(null);
                return;
            }
            setParticipationLoading(true);
            try {
                const resp = await getRequest({
                    url: `${API_BASE_URL}/api/skill-evaluations/campagne/${campagneId}/participation`,
                    token: sessionStorage.getItem("token"),
                });
                if (!cancelled) setParticipation(resp.data);
            } catch (error) {
                if (!cancelled) {
                    setParticipation(null);
                    Swal.fire({ icon: "error", text: error.response?.data?.message || "Erreur chargement participation", timer: 1800, showConfirmButton: false });
                }
            } finally {
                if (!cancelled) setParticipationLoading(false);
            }
        };
        loadParticipation();
        return () => {
            cancelled = true;
        };
    }, [campagneId]);

    const rows = useMemo(() => {
        return (evaluations || []).map((e) => {
            const niveaux = e.niveaux || [];
            const skillNames = niveaux.map((n) => n.skillNom || n.skillCode).filter(Boolean);
            const domains = [...new Set(niveaux.map((n) => n.domaineNom).filter(Boolean))];
            const themes = [...new Set(niveaux.map((n) => n.themeNom || n.themeName || n.theme).filter(Boolean))];
            return {
                id: e.id,
                raw: e,
                name: e.userFullName || "-",
                userId: e.userId,
                reference: e.campagneReference || selectedCampagne?.reference || "-",
                evaluator: e.evaluateurFullName || "Auto-évaluation",
                skills: skillNames.join(", ") || "-",
                domains: domains.join(", ") || "-",
                themes: themes.join(", ") || "-",
                statusCode: String(e.statut || "").toUpperCase(),
                status: statusLabel(e.statut),
                date: e.dateEvaluation || e.updatedAt || e.createdAt,
                score: averageScore(niveaux),
            };
        });
    }, [evaluations, selectedCampagne]);

    const filterOptions = useMemo(() => {
        const niveaux = (evaluations || []).flatMap((e) => e.niveaux || []);
        return {
            collaborateurs: uniqueOptions(rows.map((r) => r.name).filter((name) => name !== "-")),
            references: uniqueOptions(rows.map((r) => r.reference).filter((ref) => ref !== "-")),
            domaines: uniqueOptions(niveaux.map((n) => n.domaineNom)),
            themes: uniqueOptions(niveaux.map((n) => n.themeNom || n.themeName || n.theme)),
            competences: uniqueOptions(niveaux.map((n) => n.skillNom || n.skillCode)),
            statuts: uniqueOptions(rows.map((r) => r.status).filter((status) => status !== "-")),
        };
    }, [evaluations, rows]);

    const filtered = useMemo(() => {
        let out = rows;
        if (collaborateur) out = out.filter((r) => norm(r.name) === norm(collaborateur));
        if (reference) out = out.filter((r) => norm(r.reference) === norm(reference));
        if (domaine) out = out.filter((r) => norm(r.domains).split(",").map((v) => v.trim()).includes(norm(domaine)));
        if (theme) out = out.filter((r) => norm(r.themes).split(",").map((v) => v.trim()).includes(norm(theme)));
        if (competence) out = out.filter((r) => norm(r.skills).split(",").map((v) => v.trim()).includes(norm(competence)));
        if (statut) out = out.filter((r) => norm(r.status) === norm(statut));
        if (dateEvaluation) {
            const min = new Date(`${dateEvaluation}T00:00:00`);
            out = out.filter((r) => {
                const d = safeDate(r.date);
                return d && d >= min;
            });
        }
        return [...out].sort((a, b) => (safeDate(b.date)?.getTime() || 0) - (safeDate(a.date)?.getTime() || 0));
    }, [rows, collaborateur, reference, domaine, theme, competence, statut, dateEvaluation]);

    useEffect(() => setPage(1), [campagneId, collaborateur, reference, domaine, theme, competence, statut, dateEvaluation]);

    const PAGE_SIZE = 10;
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageSafe = Math.min(Math.max(page, 1), totalPages);
    const pageItems = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

    const inProgress = filtered.filter((r) => ["EN_ATTENTE_COLLAB", "EN_ATTENTE_MANAGER", "EN_ATTENTE_N2", "PROPOSE"].includes(r.statusCode));
    const validated = filtered.filter((r) => r.statusCode === "VALIDEE");
    const rejected = filtered.filter((r) => r.statusCode === "REJETEE");

    const eligibleUsers = useMemo(() => {
        if (!Array.isArray(participation?.eligibleUsers)) return [];
        return participation.eligibleUsers.map((u) => ({
            id: u.id,
            name: userName(u),
            entityName: u.entityName,
            jobName: u.jobName,
            managerName: u.managerName,
        }));
    }, [participation]);

    const participants = participation?.participantsCount ?? new Set(filtered.map((r) => r.userId).filter(Boolean)).size;
    const populationCount = participation?.eligibleCount ?? eligibleUsers.length;
    const participationRate = participation?.participationRate ?? (populationCount ? Math.round((participants * 100) / populationCount) : 0);
    const activeFiltersCount = [collaborateur, reference, domaine, theme, competence, statut, dateEvaluation].filter(Boolean).length;

    const activeStatsRows = useMemo(() => {
        if (statsModalTitle === "Evaluations en cours") return inProgress;
        if (statsModalTitle === "Evaluations validées") return validated;
        if (statsModalTitle === "Evaluations rejetées") return rejected;
        return [];
    }, [statsModalTitle, inProgress, validated, rejected]);

    const resetFilters = () => {
        setCollaborateur("");
        setReference("");
        setDomaine("");
        setTheme("");
        setCompetence("");
        setStatut("");
        setDateEvaluation("");
    };

    const submitProlonger = async () => {
        if (!campagneId || !newEndDate) return;
        setActionLoading(true);
        try {
            await postRequest({
                url: `${API_BASE_URL}/api/campagnes/${campagneId}/prolonger`,
                token: sessionStorage.getItem("token"),
                data: { newEndDate, comment: prolongerComment },
            });
            setOpenProlonger(false);
            setNewEndDate("");
            setProlongerComment("");
            dispatch(fetchCampagnesRequest());
            refreshData();
            Swal.fire({ icon: "success", text: "Campagne prolongée avec succès.", timer: 1800, showConfirmButton: false });
        } catch (error) {
            Swal.fire({ icon: "error", text: error.response?.data?.message || "Erreur prolongation campagne", timer: 2400, showConfirmButton: false });
        } finally {
            setActionLoading(false);
        }
    };

    const submitRelancer = async () => {
        if (!campagneId) return;
        setActionLoading(true);
        try {
            const resp = await postRequest({
                url: `${API_BASE_URL}/api/skill-evaluations/campagne/${campagneId}/relance-participation`,
                token: sessionStorage.getItem("token"),
                data: {},
            });
            setOpenRelancer(false);
            Swal.fire({ icon: "success", text: resp.data?.message || "Relance envoyée.", timer: 1800, showConfirmButton: false });
        } catch (error) {
            Swal.fire({ icon: "error", text: error.response?.data?.message || "Erreur relance participation", timer: 2400, showConfirmButton: false });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRelanceWorkflowCampagne = async (event) => {
        event?.stopPropagation?.();
        if (!campagneId || actionLoading || inProgress.length === 0) return;
        const result = await Swal.fire({
            icon: "question",
            text: "Voulez-vous envoyer un email de relance aux validateurs pour soumettre leurs évaluations ?",
            showCancelButton: true,
            confirmButtonText: "Confirmer",
            cancelButtonText: "Annuler",
        });
        if (!result.isConfirmed || !campagneId) return;
        setActionLoading(true);
        try {
            const resp = await postRequest({
                url: `${API_BASE_URL}/api/skill-evaluations/campagne/${campagneId}/relance-workflow`,
                token: sessionStorage.getItem("token"),
                data: {},
            });
            Swal.fire({ icon: "success", text: resp.data?.message || "Relance envoyée.", timer: 1800, showConfirmButton: false });
        } catch (error) {
            Swal.fire({ icon: "error", text: error.response?.data?.message || "Erreur relance validateurs", timer: 2400, showConfirmButton: false });
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="consPage consSkillsPage">
            <div className="consSkillsHeader">
                <div>
                    <h1>Consolidation des compétences</h1>
                    <p>Suivez les évaluations, les statuts de validation et la participation par campagne.</p>
                </div>
                <div className="consSkillsHeaderActions">
                    <span className="consSkillsBadge">{filtered.length} évaluation{filtered.length > 1 ? "s" : ""}</span>
                </div>
            </div>

            <div className="consRow0">
                <div className="consCampagneSelect">
                    <select value={campagneId} onChange={(e) => setCampagneId(e.target.value)} disabled={!campagnes.length}>
                        {!campagnes.length && <option value="">Aucune campagne compétences disponible</option>}
                        {campagnes.map((c) => (
                            <option key={c.id} value={String(c.id)}>
                                {c.nom || `Campagne ${c.id}`}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="consRightPanel">
                    <div className="consRightRow">
                        <div className="consRightLabel">Population cible</div>
                        <button className="chipBtn" type="button" onClick={() => setOpenUsersModal(true)}>
                            TOUS LES COLLABORATEURS
                        </button>
                    </div>
                    <div className="consRightRow">
                        <div className="consRightLabel">Taux de participation</div>
                        <button
                            type="button"
                            className={`rateBadge ${participationRate < 30 ? "is-low" : participationRate < 70 ? "is-mid" : "is-high"}`}
                            onClick={() => setOpenParticipationModal(true)}
                        >
                            {participationLoading ? "..." : `${participationRate}%`}
                        </button>
                    </div>
                </div>
            </div>

            <div className="consTop">
                <div className="consRange">
                    <Calendar size={18} />
                    <span>Du : </span>
                    <input value={selectedCampagne?.dateDebut ? fmtDateFR(selectedCampagne.dateDebut).split("/").reverse().join("-") : ""} readOnly disabled />
                    <span className="toTxt">au :</span>
                    <input value={selectedCampagne?.dateFin ? fmtDateFR(selectedCampagne.dateFin).split("/").reverse().join("-") : ""} readOnly disabled />
                </div>
                <div className="consTopBtns">
                    <button className="btnTop btnDark" type="button" onClick={refreshData} disabled={!campagneId || loading}>
                        <RefreshCcw size={16} /> Actualiser
                    </button>
                    <button className="btnTop btnRed" type="button" onClick={() => setOpenProlonger(true)} disabled={!campagneId || actionLoading}>
                        Prolonger
                    </button>
                    <button className="btnTop btnBlue" type="button" onClick={() => setOpenRelancer(true)} disabled={!campagneId || actionLoading}>
                        Relancer
                    </button>
                </div>
            </div>

            <div className="consStats">
                <button type="button" className="statItem statBtn" onClick={() => { setStatsModalTitle("Evaluations en cours"); setOpenStatsModal(true); }}>
                    <div className="statWithAction">
                        <div>
                            <div className="statNum cBlue">{inProgress.length}</div>
                            <div className="statTxt"><Clock3 size={16} className="cBlue" />Evaluations en cours</div>
                        </div>
                        <span
                            className="relanceBtn statRelanceBtn"
                            role="button"
                            tabIndex={0}
                            onClick={handleRelanceWorkflowCampagne}
                            title="Relancer les validateurs"
                            aria-disabled={!campagneId || actionLoading || inProgress.length === 0}
                        >
                            <BellRing size={18} />
                        </span>
                    </div>
                </button>
                <button type="button" className="statItem statBtn" onClick={() => { setStatsModalTitle("Evaluations validées"); setOpenStatsModal(true); }}>
                    <div className="statNum cGreen">{validated.length}</div>
                    <div className="statTxt"><CheckCircle2 size={16} className="cGreen" />Evaluations validées</div>
                </button>
                <button type="button" className="statItem statBtn" onClick={() => { setStatsModalTitle("Evaluations rejetées"); setOpenStatsModal(true); }}>
                    <div className="statNum cRed">{rejected.length}</div>
                    <div className="statTxt"><XCircle size={16} className="cRed" />Evaluations rejetées</div>
                </button>
                <button type="button" className="statItem statBtn" onClick={() => setOpenParticipationModal(true)}>
                    <div className="statNum cYellow">{participants}</div>
                    <div className="statTxt"><Users size={16} className="cYellow" />Participants</div>
                </button>
            </div>

            <div className="consFiltersToggle">
                <button className="btnToggleFilters" type="button" onClick={() => setShowFilters(!showFilters)}>
                    <Filter size={16} />
                    {showFilters ? "Masquer les filtres de recherche" : "Afficher les filtres de recherche"}
                    {activeFiltersCount > 0 && <span className="filterCount">{activeFiltersCount}</span>}
                </button>
            </div>

            {showFilters && (
                <div className="consFiltersBox">
                    <div className="filtersHeaderLine">
                        <div className="line" />
                        <div className="filtersTitle">Filtres de recherche</div>
                        <div className="line lineBlue" />
                    </div>
                    <div className="filtersGrid">
                        <div className="fField">
                            <label>Collaborateur</label>
                            <select value={collaborateur} onChange={(e) => setCollaborateur(e.target.value)}>
                                <option value=""></option>
                                {filterOptions.collaborateurs.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </div>
                        <div className="fField">
                            <label>Référence campagne</label>
                            <select value={reference} onChange={(e) => setReference(e.target.value)}>
                                <option value=""></option>
                                {filterOptions.references.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </div>
                        <div className="fField">
                            <label>Domaine</label>
                            <select value={domaine} onChange={(e) => setDomaine(e.target.value)}>
                                <option value=""></option>
                                {filterOptions.domaines.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </div>
                        <div className="fField">
                            <label>Thème</label>
                            <select value={theme} onChange={(e) => setTheme(e.target.value)}>
                                <option value=""></option>
                                {filterOptions.themes.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </div>
                        <div className="fField">
                            <label>Compétence</label>
                            <select value={competence} onChange={(e) => setCompetence(e.target.value)}>
                                <option value=""></option>
                                {filterOptions.competences.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </div>
                        <div className="fField">
                            <label>Statut</label>
                            <select value={statut} onChange={(e) => setStatut(e.target.value)}>
                                <option value=""></option>
                                {filterOptions.statuts.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </div>
                        <div className="fField">
                            <label>Date évaluation</label>
                            <input type="date" value={dateEvaluation} onChange={(e) => setDateEvaluation(e.target.value)} />
                        </div>
                    </div>
                    <div className="filtersReset">
                        <button className="btnReset" type="button" onClick={resetFilters}>Réinitialiser les valeurs</button>
                    </div>
                </div>
            )}

            <div className="consTableWrap">
                <table className="consTable">
                    <thead>
                    <tr>
                        <th>Collaborateur</th>
                        <th>Référence</th>
                        <th>Evaluateur</th>
                        <th>Compétences</th>
                        <th>Domaine</th>
                        <th className="center">Score moyen</th>
                        <th className="center">Statut</th>
                        <th className="center">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {!campagneId ? (
                        <tr><td colSpan={8} className="emptyCell">Aucune campagne compétences disponible.</td></tr>
                    ) : loading ? (
                        <tr><td colSpan={8} className="emptyCell">Chargement...</td></tr>
                    ) : pageItems.length === 0 ? (
                        <tr><td colSpan={8} className="emptyCell">Aucune évaluation.</td></tr>
                    ) : pageItems.map((row) => (
                        <tr key={row.id}>
                            <td>{row.name}</td>
                            <td>{row.reference}</td>
                            <td>{row.evaluator}</td>
                            <td>{row.skills}</td>
                            <td>{row.domains}</td>
                            <td className="center">{row.score ?? "-"}</td>
                            <td className={`center ${statusClass(row.statusCode)}`}>{row.status}</td>
                            <td className="center">
                                <button className="eyeBtn" type="button" onClick={() => setSelectedRow(row.raw)} title="Afficher">
                                    <Eye size={18} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                <div className="pager">
                    <button className="btnPager" disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Précédent</button>
                    <div className="pagerInfo">Page <b>{pageSafe}</b> / {totalPages} - <span className="muted">{filtered.length} évaluations</span></div>
                    <button className="btnPager" disabled={pageSafe >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Suivant</button>
                </div>
            </div>

            {selectedRow && (
                <ModalShell
                    open={!!selectedRow}
                    title={`Détails évaluation #${selectedRow?.id ?? "-"}`}
                    onClose={() => setSelectedRow(null)}
                    footer={<button className="btnPager" type="button" onClick={() => setSelectedRow(null)}>Fermer</button>}
                >
                    <EvaluationDetailView data={selectedRow} />
                </ModalShell>
            )}

            <ModalShell
                open={openProlonger}
                title="Prolonger la campagne"
                onClose={() => setOpenProlonger(false)}
                footer={
                    <>
                        <button className="btnPager" type="button" onClick={() => setOpenProlonger(false)}>Annuler</button>
                        <button className="btnTop btnRed" type="button" onClick={submitProlonger} disabled={!campagneId || !newEndDate || actionLoading}>Confirmer</button>
                    </>
                }
            >
                <div className="formGrid2">
                    <div className="fField">
                        <label>Nouvelle date de fin</label>
                        <input type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
                    </div>
                    <div className="fField">
                        <label>Commentaire</label>
                        <input value={prolongerComment} onChange={(e) => setProlongerComment(e.target.value)} placeholder="Optionnel..." />
                    </div>
                </div>
                <div className="miniNote">Les personnes concernées seront notifiées de la prolongation.</div>
            </ModalShell>

            <ModalShell
                open={openRelancer}
                title="Relancer"
                onClose={() => setOpenRelancer(false)}
                footer={
                    <>
                        <button className="btnPager" type="button" onClick={() => setOpenRelancer(false)}>Annuler</button>
                        <button className="btnTop btnBlue" type="button" onClick={submitRelancer} disabled={!campagneId || actionLoading}>Envoyer</button>
                    </>
                }
            >
                <div className="miniNote">
                    Cette action relancera les collaborateurs éligibles qui n'ont pas encore participé à cette campagne.
                    {participation ? <> <b>{participation?.notParticipatedUsers?.length ?? Math.max(0, populationCount - participants)}</b> personne(s) concernée(s).</> : null}
                </div>
            </ModalShell>

            <ModalShell
                open={openUsersModal}
                title="Population cible"
                onClose={() => setOpenUsersModal(false)}
                footer={<button className="btnPager" type="button" onClick={() => setOpenUsersModal(false)}>Fermer</button>}
            >
                <div className="miniNote">Campagne : <b>{selectedCampagne?.nom || "-"}</b> - Total : <b>{eligibleUsers.length}</b></div>
                <div className="miniTableWrap">
                    <table className="miniTable">
                        <thead>
                        <tr>
                            <th>Collaborateur</th>
                            <th>Entité</th>
                            <th>Poste</th>
                            <th>Manager</th>
                            <th className="center">Evaluations</th>
                        </tr>
                        </thead>
                        <tbody>
                        {eligibleUsers.map((u) => {
                            const count = rows.filter((r) => String(r.userId) === String(u.id)).length;
                            return (
                                <tr key={u.id}>
                                    <td>{u.name}</td>
                                    <td>{u.entityName || "-"}</td>
                                    <td>{u.jobName || "-"}</td>
                                    <td>{u.managerName || "-"}</td>
                                    <td className="center">{count}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </ModalShell>

            <ModalShell
                open={openParticipationModal}
                title="Taux de participation"
                onClose={() => setOpenParticipationModal(false)}
                footer={<button className="btnPager" type="button" onClick={() => setOpenParticipationModal(false)}>Fermer</button>}
            >
                <div className="participationBox">
                    <div className="participationRow">
                        <div className="pLeft"><Users size={16} /> Participants</div>
                        <div className="pRight"><b>{participants}</b></div>
                    </div>
                    <div className="participationRow">
                        <div className="pLeft"><Percent size={16} /> Population cible</div>
                        <div className="pRight"><b>{populationCount}</b></div>
                    </div>
                    <div className="participationRow">
                        <div className="pLeft"><Percent size={16} /> Non participants</div>
                        <div className="pRight"><b>{participation?.notParticipatedUsers?.length ?? Math.max(0, populationCount - participants)}</b></div>
                    </div>
                    <div className="participationRow">
                        <div className="pLeft"><Percent size={16} /> Taux</div>
                        <div className="pRight"><span className="rateBig">{participationRate}%</span></div>
                    </div>
                </div>
            </ModalShell>

            <ModalShell
                open={openStatsModal}
                title={statsModalTitle}
                onClose={() => setOpenStatsModal(false)}
                footer={<button className="btnPager" type="button" onClick={() => setOpenStatsModal(false)}>Fermer</button>}
            >
                <div className="miniTableWrap">
                    <table className="miniTable">
                        <thead>
                        <tr>
                            <th>Collaborateur</th>
                            <th>Référence</th>
                            <th>Evaluateur</th>
                            <th className="center">Score moyen</th>
                            <th className="center">Statut</th>
                        </tr>
                        </thead>
                        <tbody>
                        {activeStatsRows.map((r) => (
                            <tr key={r.id}>
                                <td>{r.name}</td>
                                <td>{r.reference}</td>
                                <td>{r.evaluator}</td>
                                <td className="center">{r.score ?? "-"}</td>
                                <td className={`center ${statusClass(r.statusCode)}`}>{r.status}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </ModalShell>
        </div>
    );
}
